import { TanStackDevtools } from '@tanstack/react-devtools';
import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { createRootRouteWithContext, HeadContent, Outlet, Scripts } from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { ToolCase } from 'lucide-react';

// @ts-ignore -- standard CSS import
import appCss from '../styles.css?url';

import { clientEnv } from '@/env';
import { DEFAULT_LOCALE } from '@/i18n';
import { ErrorBoundary, ErrorFallback } from '@/modus/ErrorBoundary';
import { LoadingSplash } from '@/modus/LoadingSplash';
import { Toast } from '@/modus/Toast';
import { Providers } from '@/providers';
import { Button } from '@/ui/base/button';

type RouterContext = {
  queryClient: QueryClient;
};

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'Tessera Labs' },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'manifest', href: '/manifest.json' },
      { rel: 'icon', href: '/favicon.ico' },
      { rel: 'icon', href: '/icon0.svg', type: 'image/svg+xml', sizes: 'any' },
      { rel: 'icon', href: '/icon1.png', type: 'image/png', sizes: '96x96' },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png', sizes: '180x180' },
    ],
  }),
  component: RootLayout,
  pendingComponent: () => <LoadingSplash />,
});

export function RootLayout() {
  const { queryClient } = Route.useRouteContext();

  return (
    <html lang={DEFAULT_LOCALE}>
      <head>
        <HeadContent />
      </head>
      <body>
        <ErrorBoundary fallback={(error) => <ErrorFallback error={error} root />}>
          <Providers queryClient={queryClient}>
            <Outlet />
            {/* left offset clears the sidebar rail; it applies only to left-anchored
                toasts (notifications), not the default bottom-centre ones. */}
            <Toast offset={{ left: '4rem' }} />
            <Scripts />
            {clientEnv.VITE_ENABLE_DEVTOOLS && (
              <TanStackDevtools
                config={{
                  theme: 'dark',
                  urlFlag: 'devtools',
                  customTrigger: (
                    <Button size="icon" variant="outline">
                      <ToolCase />
                    </Button>
                  ),
                }}
                plugins={[
                  {
                    name: 'TanStack Query',
                    render: <ReactQueryDevtoolsPanel />,
                  },
                  {
                    name: 'TanStack Router',
                    render: <TanStackRouterDevtoolsPanel />,
                  },
                ]}
              />
            )}
          </Providers>
        </ErrorBoundary>
      </body>
    </html>
  );
}
